import React, { useEffect, useRef } from 'react';
import './snowfield.css';

const FLAKES_PER_MEGAPIXEL = 46;
const MAX_FLAKES = 130;
const SPRITE_SIZE = 32;

const createFlake = (width, height, seedY) => {
  const depth = Math.random(); // 0 = far and slow, 1 = near and fast
  return {
    x: Math.random() * width,
    y: seedY ? Math.random() * height : -24,
    radius: 1.1 + depth * 3.2,
    fall: 12 + depth * 38,
    drift: (Math.random() - 0.5) * 18,
    sway: 0.4 + Math.random() * 0.9,
    phase: Math.random() * Math.PI * 2,
    alpha: 0.1 + depth * 0.32,
  };
};

/**
 * Flakes are drawn from a soft radial sprite instead of a filled arc. Hard-edged
 * dots on a near-black page read as dead pixels or compression noise; a feathered
 * edge reads as snow that is out of focus, which is what it should be.
 */
const createSprite = () => {
  const sprite = document.createElement('canvas');
  sprite.width = SPRITE_SIZE;
  sprite.height = SPRITE_SIZE;
  const context = sprite.getContext('2d');
  const half = SPRITE_SIZE / 2;
  const gradient = context.createRadialGradient(half, half, 0, half, half, half);
  gradient.addColorStop(0, 'rgba(255, 249, 242, 1)');
  gradient.addColorStop(0.35, 'rgba(255, 249, 242, 0.7)');
  gradient.addColorStop(1, 'rgba(255, 249, 242, 0)');
  context.fillStyle = gradient;
  context.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);
  return sprite;
};

/**
 * Ambient snowfall behind the whole site. Kept deliberately cheap: one canvas,
 * no per-flake DOM, paused when the tab is hidden, and skipped entirely for
 * visitors who ask for reduced motion.
 */
export default function Snowfield() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (motionQuery.matches) return undefined;

    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    const sprite = createSprite();
    let width = 0;
    let height = 0;
    let flakes = [];
    let frame = 0;
    let lastTime = 0;

    const resize = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      context.setTransform(ratio, 0, 0, ratio, 0, 0);

      const target = Math.min(
        MAX_FLAKES,
        Math.round(((width * height) / 1e6) * FLAKES_PER_MEGAPIXEL),
      );
      flakes = Array.from({ length: target }, () => createFlake(width, height, true));
    };

    const draw = (time) => {
      const delta = Math.min((time - lastTime) / 1000, 0.05);
      lastTime = time;
      context.clearRect(0, 0, width, height);

      flakes.forEach((flake, index) => {
        flake.y += flake.fall * delta;
        flake.phase += flake.sway * delta;
        flake.x += (flake.drift + Math.sin(flake.phase) * 12) * delta;

        if (flake.y - flake.radius > height) {
          flakes[index] = createFlake(width, height, false);
          return;
        }
        if (flake.x < -20) flake.x = width + 20;
        if (flake.x > width + 20) flake.x = -20;

        const size = flake.radius * 2;
        context.globalAlpha = flake.alpha;
        context.drawImage(sprite, flake.x - flake.radius, flake.y - flake.radius, size, size);
      });

      context.globalAlpha = 1;
      frame = window.requestAnimationFrame(draw);
    };

    const start = () => {
      lastTime = performance.now();
      frame = window.requestAnimationFrame(draw);
    };

    const onVisibilityChange = () => {
      window.cancelAnimationFrame(frame);
      if (!document.hidden) start();
    };

    resize();
    start();
    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return <canvas ref={canvasRef} className="snowfield" aria-hidden="true" />;
}
